import { useMemo } from "react"

import { useCustomerCompanyRetrieve } from "@boundless/bl-api-client/customer/services"
import { createBrowserInspector } from "@statelyai/inspect"
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router"
import { z } from "zod"
import { useStore } from "zustand"

import { FlowProgressBar } from "@/components/flow-progress-bar/FlowProgressBar"
import {
  createMachineWithInitialState,
  initialStateFromRouteMatch,
} from "@/lib/flows/base/utils"
import {
  CompanyFlowContext,
  companyFlowMachine,
  companyFlowSteps,
} from "@/lib/flows/company"
import { store } from "@/lib/store"

export const Route = createFileRoute("/_dashboard/company")({
  component: RouteComponent,
  validateSearch: z.object({
    returnTo: z.string().optional(),
  }),
  beforeLoad: ({ context: { store } }) => {
    if (!["customer", "partner"].includes(store.getState().userRole || "")) {
      throw redirect({ to: "/" })
    }

    const companyUuid = store.getState().activeCompanyUuid
    return { companyUuid }
  },
})

export function RouteComponent() {
  const navigate = useNavigate()
  const { returnTo } = Route.useSearch()
  const matchRoute = useMatchRoute()
  const companyUuid = useStore(store, (state) => state.activeCompanyUuid)
  const { data: company } = useCustomerCompanyRetrieve(companyUuid ?? "", {
    query: {
      enabled: !!companyUuid,
    },
  })

  const { machineLogic, machineOptions } = useMemo(() => {
    const machineLogic = createMachineWithInitialState(companyFlowMachine, () =>
      initialStateFromRouteMatch(companyFlowMachine, matchRoute),
    )

    const inspector =
      import.meta.env.DEV && import.meta.env.VITE_ENABLE_XSTATE_INSPECT
        ? createBrowserInspector()
        : undefined
    const inspect = inspector?.inspect

    const overrideHandlers = returnTo
      ? {
          onNextOverride: () => navigate({ to: returnTo }),
          onBackOverride: () => navigate({ to: returnTo }),
        }
      : {}

    const machineOptions = {
      input: {
        navigate,
        hasMultipleOwners: company?.hasMultipleOwners ?? false,
        initialHasMultipleOwners: company?.hasMultipleOwners ?? false,
        requiresExtraDetails: company?.requiresExtraDetails ?? false,
        ...overrideHandlers,
      },
      id: "company-flow",
      inspect,
    }

    return {
      machineLogic,
      machineOptions,
    }
  }, [matchRoute, navigate, company?.hasMultipleOwners, returnTo])

  return (
    <CompanyFlowContext.Provider logic={machineLogic} options={machineOptions}>
      <div className="flex min-w-0 flex-col gap-4">
        <FlowProgressBar
          FlowContext={CompanyFlowContext}
          flowSteps={companyFlowSteps}
        />
        <Outlet />
      </div>
    </CompanyFlowContext.Provider>
  )
}
